import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { vehicleKeys } from './use-vehicles-query'
import { vehicleGpsApi } from '@/lib/api/vehicle-gps-api'
import { m } from '@/lib/paraglide/messages'

export const vehicleGpsKeys = {
    all: ['vehicle-gps'] as const,
    detail: (vehicleId: string) =>
        [...vehicleGpsKeys.all, 'detail', vehicleId] as const,
    playback: (vehicleId: string, start: string, end: string) =>
        [...vehicleGpsKeys.all, 'playback', vehicleId, start, end] as const,
    cameras: (vehicleId: string) =>
        [...vehicleGpsKeys.all, 'cameras', vehicleId] as const,
}

export function useVehicleGps(vehicleId: string, enabled = true) {
    return useQuery({
        queryKey: vehicleGpsKeys.detail(vehicleId),
        queryFn: async () => {
            const res = await vehicleGpsApi.detail(vehicleId)
            return res.data ?? null
        },
        enabled: enabled && Boolean(vehicleId),
        refetchInterval: 20_000,
        refetchIntervalInBackground: false,
    })
}

export function useResolveVehicleGps(vehicleId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: () => vehicleGpsApi.resolve(vehicleId),
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: vehicleGpsKeys.detail(vehicleId),
            })
            void qc.invalidateQueries({
                queryKey: vehicleKeys.detail(vehicleId),
            })
            toast.success(m.vehicle_gps_resolve_success())
        },
        onError: () => toast.error(m.vehicle_gps_resolve_error()),
    })
}

export function useVehicleGpsPlayback(
    vehicleId: string,
    start: string,
    end: string,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: vehicleGpsKeys.playback(vehicleId, start, end),
        queryFn: async () => {
            const res = await vehicleGpsApi.playback(vehicleId, start, end)
            return res.data ?? []
        },
        enabled:
            (options?.enabled ?? true) &&
            Boolean(vehicleId) &&
            Boolean(start) &&
            Boolean(end),
    })
}

export function useVehicleCameraLatest(vehicleId: string, enabled = true) {
    return useQuery({
        queryKey: vehicleGpsKeys.cameras(vehicleId),
        queryFn: async () => {
            const res = await vehicleGpsApi.latestCamera(vehicleId)
            return res.data ?? null
        },
        enabled: enabled && Boolean(vehicleId),
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
    })
}
